import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { GraduationCap, BookOpen, Award, Clock, Play, CheckCircle2, UserPlus, Plus, Upload, FileVideo, Presentation, FileText as FileIcon, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const DEPTS = ["Medical Coding", "Home Health Medical Coding", "Engineering", "Human Resources", "Business Development", "Finance", "IT Operations", "Sales"];
const ROLES = ["super_admin", "hr", "recruiter", "manager", "employee", "it", "finance"];

export default function Learning() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCourse, setAssignCourse] = useState(null);
  const [assignDept, setAssignDept] = useState("any");
  const [assignRole, setAssignRole] = useState("any");
  const [assignEmails, setAssignEmails] = useState("");
  const [assigning, setAssigning] = useState(false);

  const canAssign = ["super_admin", "hr", "manager"].includes(user?.role);
  const canCreate = canAssign;

  // Course creation
  const [createOpen, setCreateOpen] = useState(false);
  const [newCourse, setNewCourse] = useState({
    title: "", category: "Medical Coding", description: "", instructor: "",
    duration_hours: 1, level: "Beginner", thumbnail: "",
  });
  const [uploadedMaterials, setUploadedMaterials] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleMaterialUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post(`/files/upload?category=course_material`, fd);
        setUploadedMaterials((prev) => [...prev, { file_id: data.id, name: data.original_filename, content_type: data.content_type }]);
      }
      toast.success(`${files.length} file(s) uploaded`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submitCreate = async () => {
    if (!newCourse.title) { toast.error("Title required"); return; }
    setCreating(true);
    try {
      await api.post("/courses/create", {
        ...newCourse,
        duration_hours: Number(newCourse.duration_hours) || 1,
        material_file_ids: uploadedMaterials.map((m) => m.file_id),
      });
      toast.success("Course created");
      setCreateOpen(false);
      setNewCourse({ title: "", category: "Medical Coding", description: "", instructor: "", duration_hours: 1, level: "Beginner", thumbnail: "" });
      setUploadedMaterials([]);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setCreating(false); }
  };

  const load = async () => {
    const [c, e] = await Promise.all([api.get("/courses"), api.get("/enrollments")]);
    setCourses(c.data);
    setEnrollments(e.data);
  };

  useEffect(() => { load(); }, []);

  const myEnrollments = enrollments.filter((e) => e.employee_email === user?.email);
  const enrolledIds = new Set(myEnrollments.map((e) => e.course_id));

  const enroll = async (id) => {
    await api.post(`/courses/${id}/enroll`);
    toast.success("Enrolled");
    load();
  };
  const complete = async (id) => {
    await api.post(`/enrollments/${id}/complete`);
    toast.success("Course completed");
    load();
  };

  const openAssign = (course) => {
    setAssignCourse(course);
    setAssignDept("any");
    setAssignRole("any");
    setAssignEmails("");
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    if (!assignCourse) return;
    setAssigning(true);
    try {
      const payload = { course_id: assignCourse.id };
      const emails = assignEmails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      if (emails.length) payload.employee_emails = emails;
      else {
        if (assignDept !== "any") payload.department = assignDept;
        if (assignRole !== "any") payload.role = assignRole;
      }
      const { data } = await api.post("/courses/bulk-enroll", payload);
      toast.success(`Assigned to ${data.enrolled} employee(s) · ${data.skipped} skipped (already enrolled)`);
      setAssignOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div data-testid="learning-page">
      <PageHeader
        title="Learning & Certifications"
        subtitle="Browse the course catalog, enroll, and complete training for your role."
        action={
          canCreate ? (
            <Button data-testid="add-course" onClick={() => setCreateOpen(true)} className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]">
              <Plus className="h-4 w-4 mr-1.5" /> Add course
            </Button>
          ) : null
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Stat label="Enrolled" value={myEnrollments.length} icon={BookOpen} />
        <Stat label="Completed" value={myEnrollments.filter((e) => e.status === "completed").length} icon={Award} />
        <Stat label="In progress" value={myEnrollments.filter((e) => e.status !== "completed").length} icon={Clock} />
      </div>

      <h2 className="font-display text-xl font-semibold text-[#0F172A] mb-3">My learning</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        {myEnrollments.length === 0 ? (
          <Card className="hr-card p-6 text-sm text-gray-500 md:col-span-3">No enrollments yet. Browse the catalog below.</Card>
        ) : myEnrollments.map((e) => {
          const c = courses.find((c) => c.id === e.course_id);
          if (!c) return null;
          return (
            <Card key={e.id} className="hr-card overflow-hidden" data-testid={`my-course-${c.id}`}>
              <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url('${c.thumbnail}')` }} />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="font-medium text-sm">{c.title}</div>
                  <StatusBadge status={e.status} />
                </div>
                <div className="text-[11px] text-gray-500 mt-1">{c.category} · {c.duration_hours} hr</div>
                {e.status !== "completed" && (
                  <Button data-testid={`complete-${e.id}`} size="sm" className="mt-3 h-8 bg-[#F26B3A] hover:bg-[#d85d2f]" onClick={() => complete(e.id)}>
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark complete
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      <h2 className="font-display text-xl font-semibold text-[#0F172A] mb-3">Catalog</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {courses.map((c) => (
          <Card key={c.id} className="hr-card overflow-hidden flex flex-col" data-testid={`course-${c.id}`}>
            <div className="h-36 bg-cover bg-center relative" style={{ backgroundImage: `url('${c.thumbnail}')` }}>
              <span className="absolute top-2 left-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/90 text-[#0F172A]">
                {c.category}
              </span>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <div className="font-display font-semibold text-sm">{c.title}</div>
              <div className="text-xs text-gray-500 mt-1 line-clamp-2 flex-1">{c.description}</div>
              <div className="text-[11px] text-gray-400 mt-2">{c.instructor} · {c.duration_hours} hr · {c.level}</div>
              {enrolledIds.has(c.id) ? (
                <span className="mt-3 inline-flex items-center gap-1 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Already enrolled
                </span>
              ) : (
                <Button data-testid={`enroll-${c.id}`} size="sm" variant="outline" className="mt-3 h-8 w-fit" onClick={() => enroll(c.id)}>
                  <Play className="h-3.5 w-3.5 mr-1" /> Enroll
                </Button>
              )}
              {canAssign && (
                <Button
                  data-testid={`assign-${c.id}`}
                  size="sm"
                  variant="ghost"
                  className="h-7 w-fit mt-1 text-[#1E88E5] hover:text-[#1E88E5] hover:bg-[#1E88E5]/5 -ml-2"
                  onClick={() => openAssign(c)}
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign to team
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Create course dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) { setUploadedMaterials([]); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="add-course-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-[#F26B3A]" /> Add a new course</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Title</Label>
              <Input data-testid="course-title" className="mt-1.5" value={newCourse.title} onChange={(e) => setNewCourse({ ...newCourse, title: e.target.value })} placeholder="e.g. ICD-10-CM Coding Fundamentals" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Category</Label>
              <Select value={newCourse.category} onValueChange={(v) => setNewCourse({ ...newCourse, category: v })}>
                <SelectTrigger data-testid="course-category-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Medical Coding", "Home Health", "Quality", "Compliance", "Leadership", "Technology", "General"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Level</Label>
              <Select value={newCourse.level} onValueChange={(v) => setNewCourse({ ...newCourse, level: v })}>
                <SelectTrigger data-testid="course-level-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Beginner", "Intermediate", "Advanced"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Duration (hours)</Label>
              <Input data-testid="course-duration" type="number" min="0.5" step="0.5" className="mt-1.5" value={newCourse.duration_hours} onChange={(e) => setNewCourse({ ...newCourse, duration_hours: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Instructor</Label>
              <Input data-testid="course-instructor" className="mt-1.5" value={newCourse.instructor} onChange={(e) => setNewCourse({ ...newCourse, instructor: e.target.value })} placeholder="Internal SME / External name" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Thumbnail URL (optional)</Label>
              <Input data-testid="course-thumbnail" className="mt-1.5" value={newCourse.thumbnail} onChange={(e) => setNewCourse({ ...newCourse, thumbnail: e.target.value })} placeholder="https://..." />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
              <textarea
                data-testid="course-description"
                rows={3}
                value={newCourse.description}
                onChange={(e) => setNewCourse({ ...newCourse, description: e.target.value })}
                className="mt-1.5 w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                placeholder="What will learners gain from this course?"
              />
            </div>

            <div className="col-span-2 border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-gray-500">Course materials</Label>
                <span className="text-[11px] text-gray-400">Videos · PPT · PDF — multiple allowed</span>
              </div>
              <label className="block">
                <input
                  type="file"
                  multiple
                  accept="video/*,.ppt,.pptx,.pdf,.doc,.docx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  onChange={handleMaterialUpload}
                  className="hidden"
                  data-testid="course-materials-input"
                />
                <div className="border-2 border-dashed border-gray-200 rounded-md p-4 text-center hover:border-[#F26B3A] cursor-pointer transition-colors">
                  {uploading ? (
                    <div className="text-xs text-gray-500"><Loader2 className="h-4 w-4 animate-spin inline mr-1.5" />Uploading…</div>
                  ) : (
                    <div className="text-xs text-gray-500">
                      <Upload className="h-5 w-5 mx-auto mb-1.5 text-gray-400" />
                      Click to upload videos, PPT, or PDF
                    </div>
                  )}
                </div>
              </label>

              {uploadedMaterials.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {uploadedMaterials.map((m) => {
                    const Icon = m.content_type?.startsWith("video/") ? FileVideo
                      : m.content_type?.includes("presentation") || m.name?.match(/\.pptx?$/i) ? Presentation
                      : FileIcon;
                    return (
                      <div key={m.file_id} className="flex items-center gap-2 text-xs bg-gray-50 rounded-md px-2 py-1.5" data-testid={`uploaded-material-${m.file_id}`}>
                        <Icon className="h-4 w-4 text-[#1E88E5]" />
                        <span className="flex-1 truncate">{m.name}</span>
                        <button
                          className="text-rose-600 hover:underline text-[11px]"
                          onClick={() => setUploadedMaterials((prev) => prev.filter((x) => x.file_id !== m.file_id))}
                        >Remove</button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button data-testid="course-create-submit" onClick={submitCreate} disabled={creating} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create course"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign / bulk-enroll dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign training</DialogTitle>
          </DialogHeader>
          {assignCourse && (
            <div className="space-y-4">
              <div className="bg-[#F26B3A]/5 rounded-md p-3">
                <div className="font-display font-semibold text-sm text-[#0F172A]">{assignCourse.title}</div>
                <div className="text-[11px] text-gray-500">{assignCourse.category} · {assignCourse.duration_hours} hr · {assignCourse.level}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Department</Label>
                  <Select value={assignDept} onValueChange={setAssignDept}>
                    <SelectTrigger className="mt-1.5" data-testid="assign-dept-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">All departments</SelectItem>
                      {DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Designation / Role</Label>
                  <Select value={assignRole} onValueChange={setAssignRole}>
                    <SelectTrigger className="mt-1.5" data-testid="assign-role-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">All roles</SelectItem>
                      {ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Or pick specific emails (overrides filters)</Label>
                <Input
                  data-testid="assign-emails"
                  value={assignEmails}
                  onChange={(e) => setAssignEmails(e.target.value)}
                  placeholder="alice@solemnity.com, bob@solemnity.com"
                  className="mt-1.5"
                />
                <p className="text-[11px] text-gray-500 mt-1">Comma- or space-separated. Leave empty to use the filters above.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="assign-submit" onClick={submitAssign} disabled={assigning} className="bg-[#F26B3A] hover:bg-[#d85d2f]">
              {assigning ? "Assigning…" : "Assign training"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <Card className="hr-card p-5 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
        <div className="hr-kpi mt-1">{value}</div>
      </div>
      <div className="h-10 w-10 rounded-lg bg-[#F26B3A]/5 text-[#0F172A] grid place-items-center">
        <Icon className="h-5 w-5" />
      </div>
    </Card>
  );
}